import moment from 'moment';
import { LocalizationService } from '@progress/kendo-react-intl';

export interface DateFormattingResult {
  displayText: string;
  tooltipText: string;
}

/**
 * Smart date formatting utility that handles the common pattern of checking if a date is valid
 * and formatting it with both a display text (fromNow) and tooltip text (formatted date)
 * 
 * @param date - The date to format (can be moment object, Date, or string)
 * @param localizationService - The localization service for internationalization
 * @param enMessages - English messages object for fallback
 * @param format - Optional custom date format (defaults to 'DD/MM/YYYY @ HH:mm:ss')
 * @param neverMessageKey - Optional message key for "never" text (defaults to 'custom.never')
 * @returns Object with displayText and tooltipText
 */
export function formatSmartDate(
  date: moment.Moment | Date | string | null | undefined,
  localizationService: LocalizationService,
  enMessages: any,
  format: string = 'DD/MM/YYYY @ HH:mm:ss',
  neverMessageKey: string = 'custom.never'
): DateFormattingResult {
  // Convert to moment if it's not already
  const momentDate = moment.isMoment(date) ? date : moment(date);
  
  // Check if date is valid (greater than 1970)
  if (momentDate.isValid() && momentDate.year() > 1970) {
    return {
      displayText: momentDate.fromNow(),
      tooltipText: momentDate.format(format)
    };
  } else {
    const neverText = localizationService.toLanguageString(neverMessageKey, enMessages.custom.never);
    return {
      displayText: neverText,
      tooltipText: neverText
    };
  }
}

/**
 * Simplified version that returns just the display text
 */
export function getSmartDateDisplayText(
  date: moment.Moment | Date | string | null | undefined,
  localizationService: LocalizationService,
  enMessages: any,
  neverMessageKey: string = 'custom.never'
): string {
  return formatSmartDate(date, localizationService, enMessages, undefined, neverMessageKey).displayText;
}

/**
 * Simplified version that returns just the tooltip text
 */
export function getSmartDateTooltipText(
  date: moment.Moment | Date | string | null | undefined,
  localizationService: LocalizationService,
  enMessages: any,
  format: string = 'DD/MM/YYYY @ HH:mm:ss',
  neverMessageKey: string = 'custom.never'
): string {
  return formatSmartDate(date, localizationService, enMessages, format, neverMessageKey).tooltipText;
}

/**
 * Check if a date is valid (greater than 1970) - the common validation pattern
 */
export function isValidDate(date: moment.Moment | Date | string | null | undefined): boolean {
  const momentDate = moment.isMoment(date) ? date : moment(date);
  return momentDate.isValid() && momentDate.year() > 1970;
}

/**
 * Get a date with fallback to a default value if invalid
 */
export function getDateWithFallback<T>(
  date: moment.Moment | Date | string | null | undefined,
  fallback: T
): moment.Moment | T {
  const momentDate = moment.isMoment(date) ? date : moment(date);
  return momentDate.isValid() && momentDate.year() > 1970 ? momentDate : fallback;
}

/**
 * Check if a date is invalid (less than or equal to 1970) - for display fallbacks
 */
export function isInvalidDate(date: moment.Moment | Date | string | null | undefined): boolean {
  const momentDate = moment.isMoment(date) ? date : moment(date);
  return !momentDate.isValid() || momentDate.year() <= 1970;
}

/**
 * Format a date with fallback text if invalid
 */
export function formatDateWithFallback(
  date: moment.Moment | Date | string | null | undefined,
  fallbackText: string = "-"
): string {
  const momentDate = moment.isMoment(date) ? date : moment(date);
  if (momentDate.isValid() && momentDate.year() > 1970) {
    return momentDate.format('DD/MM/YYYY @ HH:mm:ss');
  }
  return fallbackText;
}
